import { patchApplication, postApplication } from 'api/application'
import getActiveLoans from 'api/application/getAllActiveLoans'
import getFinalDecision from 'api/application/getFinalDecision'
import { isUnauthorizedState } from 'api/auth/unauthorizedInterceptor'
import { Disclosure } from 'api/lender/getLenderDisclosures'
import { AxiosError } from 'axios'
import { FormValues } from 'containers/FormBuilder'
import {
    ApplicationDataType,
    ActiveLoanType,
} from 'containers/ReturnCustomer/types/returnCustomerTypes'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'
import SourceClient from 'state/enums/SourceClient'
import SourceSubclient from 'state/enums/SourceSubclient'
import getApplicationDecisionPath from 'utils/getApplicationDecisionPath'
import * as toast from 'utils/toast'

import createPostApplicationDTO from './AutomobileApplication/createPostApplicationDTO'
import setApplicationCollaterals from './PowersportsApplication/setApplicationCollaterals'

type SubmitPowersportsLikeApplicationOptions = {
    uuid: string
    values: FormValues
    setShowSubmissionModal: (state: boolean) => void
    setShowReturnCustomerModal: (state: boolean) => void
    setApplicationData: (state: ApplicationDataType) => void
    setActiveLoans: (state: ActiveLoanType[]) => void
    returnCustomerPsApp2FeatureFlag: boolean | undefined
    finalize: boolean
    isCopy?: boolean
    parentId?: number
    disclosures?: Disclosure[]
    useCollateralsAssetsEndpoint?: boolean
}

const tryShowReturnCustomerModal = async (
    applicationData: ApplicationDataType,
    parentId: number | undefined,
    setApplicationData: (state: ApplicationDataType) => void,
    setShowReturnCustomerModal: (state: boolean) => void,
    setActiveLoans: (state: ActiveLoanType[]) => void,
) => {
    const activeLoans = await getActiveLoans({
        id: applicationData.id,
        application_type: applicationData.application_type,
        social_security_number: applicationData.social_security_number,
        coapplicant_social_security_number:
            applicationData.coapplicant_social_security_number,
        parent_id: parentId,
    })

    if (activeLoans?.length) {
        setActiveLoans(activeLoans)
        setApplicationData(applicationData)
        setShowReturnCustomerModal(true)

        return true
    }

    return false
}

export default async function submitPowersportsLikeApplication({
    uuid,
    values,
    setShowSubmissionModal,
    setShowReturnCustomerModal,
    setApplicationData,
    setActiveLoans,
    returnCustomerPsApp2FeatureFlag,
    finalize,
    isCopy,
    parentId,
    disclosures,
    useCollateralsAssetsEndpoint = false,
}: SubmitPowersportsLikeApplicationOptions): Promise<string> {
    let redirectUrl
    let targetUuid: string = uuid

    try {
        const applicationDTO = createPostApplicationDTO(
            values,
            isCopy,
            parentId,
            SourceClient.OCTANE_EXTENSION,
            SourceSubclient.POWERSPORTS_APP_V2,
            disclosures,
        )

        let applicationData
        let deleteExistingCollaterals = false

        if (targetUuid) {
            applicationData = await patchApplication(applicationDTO, targetUuid)
            deleteExistingCollaterals = true
        } else {
            applicationData = await postApplication(applicationDTO)
            targetUuid = applicationData.uuid
        }

        await setApplicationCollaterals(
            targetUuid,
            values.vehicle,
            deleteExistingCollaterals,
            useCollateralsAssetsEndpoint,
        )

        if (finalize) {
            if (returnCustomerPsApp2FeatureFlag) {
                if (
                    await tryShowReturnCustomerModal(
                        applicationData,
                        parentId,
                        setApplicationData,
                        setShowReturnCustomerModal,
                        setActiveLoans,
                    )
                ) {
                    return targetUuid
                }
            }

            setShowSubmissionModal(true)
            const decisionResult = await getFinalDecision(targetUuid)

            // Use application ID from polling response if available (async), otherwise use ID from post/patch
            const applicationId =
                decisionResult.applicationId ?? applicationData.id

            redirectUrl = getApplicationDecisionPath(applicationId, false)
        } else {
            toast.success({
                message: `Application saved`,
                withCloseIcon: false,
            })
        }
    } catch (error) {
        setShowSubmissionModal(false)
        setShowReturnCustomerModal(false)

        if (!isUnauthorizedState()) {
            OctaneLogger.axiosError(
                'Unexpected error when trying to submit an application.',
                error as AxiosError,
                {
                    applicationUuid: uuid,
                    isCopy,
                    parentId,
                },
            )

            redirectUrl = `#/saved-applications`

            toast.error({
                message: `An unexpected error occurred. Please try again or contact support.`,
                withCloseIcon: false,
            })
        }
    }

    if (redirectUrl) {
        window.location.hash = redirectUrl
    }

    return targetUuid
}
